import { useState, useEffect } from 'react';
import { manufacturerSettingsAPI } from '../../services/api';

const TRIGGER_OPTIONS = [
	{ value: 'CREATED', label: 'When order is created', description: 'Immediate quote request' },
	{ value: 'FACTORY_REVIEWED', label: 'When factory reviews order', description: 'After factory acknowledges' },
	{ value: 'FACTORY_CONFIRMED', label: 'When factory confirms order', description: 'After factory acceptance' },
	{ value: 'ACCEPTED', label: 'When order is accepted', description: 'Final acceptance status' },
	{ value: 'MANUAL_ONLY', label: 'Manual only', description: 'Ops must trigger manually' }
];

export default function ManufacturerQuoteSettings({ manufacturerId, manufacturerName }) {
	const [settings, setSettings] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);

	useEffect(() => {
		loadSettings();
	}, [manufacturerId]);

	const loadSettings = async () => {
		try {
			setLoading(true);
			setError(null);
			const response = await manufacturerSettingsAPI.getSettings(manufacturerId);
			setSettings(response.data);
		} catch (err) {
			setError('Failed to load settings: ' + (err.message || 'Unknown error'));
		} finally {
			setLoading(false);
		}
	};

	const handleChange = (field, value) => {
		setSettings(prev => ({ ...prev, [field]: value }));
		setHasChanges(true);
		setSuccess(false);
	};

	const handleSave = async () => {
		try {
			setSaving(true);
			setError(null);
			await manufacturerSettingsAPI.updateSettings(manufacturerId, {
				factoryInitiatedTrigger: settings.factoryInitiatedTrigger,
				buyerInitiatedTrigger: settings.buyerInitiatedTrigger,
				autoQuoteEnabled: settings.autoQuoteEnabled,
				minOrderValue: settings.minOrderValue ? parseFloat(settings.minOrderValue) : null,
				requireHsCode: settings.requireHsCode
			});
			setSuccess(true);
			setHasChanges(false);
		} catch (err) {
			setError('Failed to save settings: ' + (err.message || 'Unknown error'));
		} finally {
			setSaving(false);
		}
	};

	const handleReset = () => {
		loadSettings();
		setHasChanges(false);
		setSuccess(false);
	};

	if (loading) {
		return <div className="loading">Loading settings...</div>;
	}

	if (!settings && !loading) {
		return (
			<div className="alert alert-error">
				Failed to load settings. Please try refreshing the page.
			</div>
		);
	}

	return (
		<div className="form-section">
			<h2>
				Quote Trigger Settings
				{manufacturerName && (
					<span className="text-muted" style={{ fontSize: '0.875rem', marginLeft: '0.5rem' }}>
						for {manufacturerName}
					</span>
				)}
			</h2>

			{error && (
				<div className="alert alert-error">{error}</div>
			)}

			{success && (
				<div className="alert alert-success">Settings saved successfully</div>
			)}

			<div style={{ marginBottom: '1.5rem' }}>
				{/* Buyer-initiated trigger */}
				<div className="form-group">
					<label>When Order is Created by BUYER</label>
					<div className="radio-group">
						{TRIGGER_OPTIONS.map(option => (
							<label key={option.value} className="radio-label">
								<input
									type="radio"
									name="buyerInitiatedTrigger"
									value={option.value}
									checked={settings.buyerInitiatedTrigger === option.value}
									onChange={(e) => handleChange('buyerInitiatedTrigger', e.target.value)}
								/>
								<span>
									<strong>{option.label}</strong>
									<br />
									<span className="text-muted">{option.description}</span>
								</span>
							</label>
						))}
					</div>
				</div>

				<hr />

				{/* Factory-initiated trigger */}
				<div className="form-group">
					<label>When Order is Created by FACTORY</label>
					<div className="radio-group">
						{TRIGGER_OPTIONS.map(option => (
							<label key={option.value} className="radio-label">
								<input
									type="radio"
									name="factoryInitiatedTrigger"
									value={option.value}
									checked={settings.factoryInitiatedTrigger === option.value}
									onChange={(e) => handleChange('factoryInitiatedTrigger', e.target.value)}
								/>
								<span>
									<strong>{option.label}</strong>
									<br />
									<span className="text-muted">{option.description}</span>
								</span>
							</label>
						))}
					</div>
				</div>

				<hr />

				{/* Min order value */}
				<div className="form-group">
					<label>Minimum Order Value for Auto-Quote</label>
					<div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
						<span>$</span>
						<input
							type="number"
							className="filter-input"
							value={settings.minOrderValue || ''}
							onChange={(e) => handleChange('minOrderValue', e.target.value)}
							placeholder="No minimum"
							style={{ width: '200px' }}
						/>
					</div>
					<span className="text-muted" style={{ fontSize: '0.75rem' }}>Leave empty for no minimum</span>
				</div>

				{/* Require HS code toggle */}
				<div className="form-group">
					<label className="checkbox-label">
						<input
							type="checkbox"
							checked={settings.requireHsCode}
							onChange={(e) => handleChange('requireHsCode', e.target.checked)}
						/>
						Require HS code before quoting
					</label>
				</div>

				{/* Auto-quote toggle */}
				<div className="form-group">
					<label className="checkbox-label">
						<input
							type="checkbox"
							checked={settings.autoQuoteEnabled}
							onChange={(e) => handleChange('autoQuoteEnabled', e.target.checked)}
						/>
						Enable automatic quote requests
					</label>
				</div>
			</div>

			<div className="form-actions">
				<button
					className="btn btn-primary"
					onClick={handleSave}
					disabled={!hasChanges || saving}
				>
					{saving ? 'Saving...' : 'Save Settings'}
				</button>
				<button
					className="btn btn-secondary"
					onClick={handleReset}
					disabled={!hasChanges || saving}
				>
					Reset
				</button>
			</div>
		</div>
	);
}
